import React, { useEffect } from "react";
import TextareaFormikInput from "../../components/inputs/TextareaFormikInput";
import Assistant from "../../components/assistant/Assistant";
import ExitButton from "../buttons/ExitButton";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { BaseModal } from "./modalStyle";
import InputImage from "../inputs/InputImage";
import {
  loadOneAxis,
  patchOneAxis,
  postAxis,
} from "../axis/one-axis/oneAxisAction";
import { loadUsers } from "../user/usersAction";
import axisSVG from "../../images/axis/axis.svg";
import { CLEAR_ONE_AXIS } from "../axis/actionTypes";
import { loadAllAxis } from "../axis/all-axis/axisAction";
import { FunctionalModal } from "./FunctionalModal";
import SendFormButton from "../buttons/SendFormButton";
import useImageChange from "../../custom-hooks/useImageChange";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { FormFormik, InputTextFormik } from "../inputs/InputStyle";
import SelectUserInputFormik from "../inputs/SelectUserInputFormik";
import CheckboxFormikInput from "../inputs/CheckboxFormikInput";

const AxisModal = ({ setOpenModal, selectedId }) => {
  const dispatch = useDispatch();

  const { users } = useSelector((state) => state.users);
  const { id, name, comment, sponsor, image, isStrategic } = useSelector(
    (state) => state.oneAxis
  );

  useEffect(() => {
    if (selectedId === 0) {
      dispatch({ type: CLEAR_ONE_AXIS });
    }
    if (users.length === 0) {
      dispatch(loadUsers());
    }
  }, [dispatch, selectedId, users.length]);

  const closeUpdate = () => {
    setOpenModal(false);
  };

  const {
    imageSize,
    lengthError,
    image: axisImage,
    onImageChange,
  } = useImageChange(id, image, axisSVG, "axis");

  const sortUsers = () => {
    let usersArray = [];
    if (selectedId) {
      users.forEach((u) => {
        if (u.id === sponsor?.id) {
          usersArray.unshift(u);
        } else {
          usersArray.push(u);
        }
      });
    } else {
      usersArray = users;
    }
    return usersArray;
  };

  const handleSubmit = async (data) => {
    let newData = Object.assign({}, data);
    if (data.toggle === false || data.sponsorId === "") {
      newData.sponsorId = null;
    } else {
      newData.sponsorId = Number(data.sponsorId);
    }
    delete newData.toggle;
    if (selectedId !== 0) {
      await dispatch(patchOneAxis(selectedId, newData));
      dispatch(loadOneAxis(selectedId));
    } else {
      await dispatch(postAxis(newData));
    }
    await dispatch(loadAllAxis());
    setOpenModal(false);
  };

  const AxisSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, "Le nom de l'axe est trop court")
      .max(255, "Le nom de l'axe est trop long")
      .required("Le nom de l'axe est requis"),
    comment: Yup.string()
      .max(4000, "le commentaire ne peut pas dépasser 4000 caractères")
  });

  return (
    <FunctionalModal
      setOpen={setOpenModal}
      component={
        <Modal>
          <Formik
            enableReinitialize
            initialValues={{
              name: selectedId ? name : "",
              comment: selectedId ? comment : "",
              toggle: selectedId && sponsor ? sponsor?.id !== 0 : false,
              sponsorId: selectedId && sponsor ? sponsor.id.toString() : "",
              isStrategic: selectedId ? isStrategic : true,
            }}
            validationSchema={AxisSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, isSubmitting, values, setFieldValue }) => (
              <>
                <FirstSection>
                  <h1>
                    {selectedId ? "Modifier l'axe : " + name : "Ajouter un axe"}{" "}
                  </h1>
                  <ExitButton closeUpdate={closeUpdate} />
                </FirstSection>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Assistant
                    context={{ object: "axis" }}
                    onSelect={(item) => {
                      setFieldValue("name", item.name);
                      setFieldValue("comment", item.description);
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 32,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <Form
                      style={{
                        width: "100%",
                      }}
                    >
                      {selectedId !== 0 && (
                        <InputImage
                          onChange={onImageChange}
                          image={axisImage}
                          name={"axe"}
                        />
                      )}
                      {lengthError && (
                        <p className={"error-text"}>
                          {" "}
                          La taille de votre image est trop importante, veuillez
                          choisir une taille inférieure à 1Mo ({imageSize})
                        </p>
                      )}

                      <div>
                        <label htmlFor="name"> Nom de l'axe * : </label>
                        <InputTextFormik
                          name="name"
                          type={"text"}
                          placeholder={"Nom de l'axe"}
                        />
                        <ErrorMessage
                          name="name"
                          render={(msg) => (
                            <div>
                              <p className="error-text"> {msg} </p>
                            </div>
                          )}
                        />
                      </div>
                      <div className={"flex-col gap-05"}>
                        <label htmlFor="comment"> Commentaire <span style={{fontSize: "12px", color:(values.comment?.length > 3500 ? values.comment?.length > 4000 ? "red" : "darkorange" : "grey")}}> { values.comment ? 4000 - (values.comment.length) : 4000} </span> </label>
                        <TextareaFormikInput
                          name="comment"
                          rows="8"
                          placeholder="Écrivez votre commentaire"
                        />
                        <ErrorMessage
                          name="comment"
                          render={(msg) => (
                            <div>
                              <p className="error-text"> {msg} </p>
                            </div>
                          )}
                        />
                      </div>
                      <div>
                        <CheckboxFormikInput
                          label={"Avec sponsor"}
                          name={"toggle"}
                        />
                        {values.toggle && (
                          <>
                            <SelectUserInputFormik
                              pilotId={values.sponsorId}
                              users={sortUsers()}
                              name={"sponsorId"}
                              label={"sponsor"}
                              initialUser={sponsor}
                            />
                            <ErrorMessage
                              name="sponsorId"
                              render={(msg) => (
                                <div>
                                  <p className="error-text"> {msg} </p>
                                </div>
                              )}
                            />
                          </>
                        )}
                      </div>
                      <CheckboxFormikInput
                        label={"Cet axe fait partie de la stratégie"}
                        name={"isStrategic"}
                      />
                      <SendFormButton
                        state={isValid && !isSubmitting}
                        actionName={selectedId === 0 ? "Ajouter" : "Modifier"}
                      />
                    </Form>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </Modal>
      }
    />
  );
};

const Modal = styled(BaseModal)`
  width: 100%;
  height: fit-content;
  overflow: auto;
  gap: 1.5rem;
  padding: 2rem;
  position: unset;
  

  @media (min-width: 768px) {
    width: 50vw;
    height: fit-content;
    left: 0;
    right: 0;
    top:0;
    bottom: 0;
    margin-top:30px;
  }
`;

const Form = styled(FormFormik)`
  gap: 2rem;
`;
const FirstSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;

  h1 {
    flex-grow: 1;
    text-align: center;
  }

  button {
    display: flex;
    align-items: flex-start;
    height: 100%;
  }
`;

export default AxisModal;
